import { resolveDateTimeMinColPct } from '@/common/utils/resolveDateTimeFieldMinWidth'

/** empty | bare N (webui %-share) | N% (always percent) | Npx (hug) */
export type TdWidthKind = 'empty' | 'share' | 'percent' | 'fixedPx'
export type TdWidthMode = 'content' | 'grow'

export interface GridRow {
  _attributes: {
    width?: string
    colspan?: string
    cols?: number
    /** Swing: non-% width (e.g. 10px) → hug content; grow → fill remainder */
    widthMode?: TdWidthMode
  }
  attrs?: any
  rows?: any[]
  element?: {
    type?: { _text?: string }
    format?: { _text?: string }
    align?: { _text?: string }
    name?: { _text?: string }
    description?: { _text?: string }
  }
  _isColspanPlaceholder?: boolean
  _isGridColumnSpacer?: boolean
}

/**
 * Raw parse (no row context):
 * - `N%` → percent (always %-weight; never hug)
 * - bare `N` → share (may become content when row has empty sibling)
 * - `Npx` → fixedPx
 * - empty → empty
 */
export function parseTdWidth(raw: unknown): { kind: TdWidthKind; value: number } {
  const s = String(raw ?? '').trim()
  if (!s) return { kind: 'empty', value: 0 }
  const lower = s.toLowerCase()
  if (lower.endsWith('px')) {
    const n = parseInt(lower.slice(0, -2), 10)
    return { kind: 'fixedPx', value: Number.isFinite(n) && n > 0 ? n : 0 }
  }
  if (lower.endsWith('%')) {
    const n = parseInt(lower.slice(0, -1), 10)
    return { kind: 'percent', value: Number.isFinite(n) && n > 0 ? n : 0 }
  }
  const n = parseInt(s, 10)
  if (Number.isFinite(n) && n > 0) return { kind: 'share', value: n }
  return { kind: 'empty', value: 0 }
}

/** Минимальные доли колонок в веб-UI (date/time — см. resolveDateTimeMinColPct) */
const LIST_LIKE_MIN_COL_PCT = 16
/** ListBox с colspan≥2  */
const LIST_LIKE_COLSPAN_MIN_COL_PCT = 22
/** Label слева от listbox */
const LABEL_BEFORE_LIST_MIN_COL_PCT = 11
/** Label справа от поля */
const LABEL_AFTER_FIELD_MIN_COL_PCT = 14
const COLWIDTH_DONOR_FLOOR_PCT = 2

function renormalizeRowColWidths(row: GridRow[]): void {
  const sum = row.reduce((acc, cell) => acc + (Number(cell?._attributes?.cols) || 0), 0)
  if (sum <= 0 || Math.abs(sum - 100) < 0.01) return
  for (const cell of row) {
    if (!cell?._attributes) continue
    const c = Number(cell._attributes.cols) || 0
    cell._attributes.cols = (100 * c) / sum
  }
}

function isColspanPlaceholder(cell: GridRow | undefined): boolean {
  return Boolean(cell?._isColspanPlaceholder)
}

function isGridColumnSpacer(cell: GridRow | undefined): boolean {
  return Boolean(cell?._isGridColumnSpacer)
}

function isRealLayoutCell(cell: GridRow | undefined): boolean {
  if (!cell) return false
  if (isColspanPlaceholder(cell) || isGridColumnSpacer(cell)) return false
  return true
}

/** Empty-width real cell → Swing grow weight 1. Spacers do not count. */
function rowHasEmptyGrowCell(row: GridRow[] | undefined): boolean {
  if (!Array.isArray(row)) return false
  return row.some(
    (cell) => isRealLayoutCell(cell) && parseTdWidth(cell._attributes?.width).kind === 'empty',
  )
}

/**
 * Effective width kind with row context.
 * Swing: non-% + empty sibling → weight 0 (hug). `N%` always stays percent.
 * Bare-only rows keep webui %-share.
 */
export function resolveTdWidthKind(
  cell: GridRow | undefined,
  row?: GridRow[],
): TdWidthKind {
  const parsed = parseTdWidth(cell?._attributes?.width)
  if (parsed.kind === 'fixedPx') return 'fixedPx'
  if (parsed.kind === 'percent') return 'percent'
  // Only bare numbers — not N% — become hug next to empty grow siblings
  if (parsed.kind === 'share' && rowHasEmptyGrowCell(row)) return 'fixedPx'
  return parsed.kind
}

function isContentWidthCell(cell: GridRow | undefined, row?: GridRow[]): boolean {
  return resolveTdWidthKind(cell, row) === 'fixedPx'
}

function isDateTimeGridCell(cell: GridRow | undefined): boolean {
  if (!cell?.element?.type) return false
  const t = String(cell.element.type._text ?? '').trim().toLowerCase()
  return t === 'date' || t === 'datetime'
}

function isListLikeGridCell(cell: GridRow | undefined): boolean {
  if (!cell?.element?.type) return false
  const t = String(cell.element.type._text ?? '').trim().toLowerCase()
  return t === 'listbox' || t === 'combobox'
}

function isProtectedMinColCell(cell: GridRow | undefined): boolean {
  return isDateTimeGridCell(cell) || isListLikeGridCell(cell)
}

function isLabelGridCell(cell: GridRow | undefined): cell is GridRow {
  if (!cell?.element?.type) return false
  const t = String(cell.element.type._text ?? '').trim().toLowerCase()
  return t === 'label'
}

function hasListLikeAhead(row: GridRow[], index: number): boolean {
  for (let j = 1; j <= 2 && index + j < row.length; j++) {
    const cell = row[index + j]
    if (isColspanPlaceholder(cell)) continue
    if (isListLikeGridCell(cell)) return true
    if (cell?.element) return false
  }
  return false
}

function isInlineFieldCell(cell: GridRow | undefined): boolean {
  if (!cell?.element?.type) return false
  const t = String(cell.element.type._text ?? '').trim().toLowerCase()
  return t === 'datetime' || t === 'date' || t === 'text' || t === 'password'
}

/** Controls that sit after a caption Label in filter-style rows (Swing preferred-size label). */
function isFieldAfterEmptyLabel(cell: GridRow | undefined): boolean {
  if (!cell?.element?.type) return false
  const t = String(cell.element.type._text ?? '').trim().toLowerCase()
  return (
    t === 'text' ||
    t === 'password' ||
    t === 'textarea' ||
    t === 'multilinetext' ||
    t === 'listbox' ||
    t === 'combobox' ||
    t === 'multiselect' ||
    t === 'date' ||
    t === 'datetime' ||
    t === 'daterange' ||
    t === 'boolean' ||
    t === 'checkbox' ||
    t === 'editable' ||
    t === 'table'
  )
}

function captionVisibleText(cell: GridRow | undefined): string {
  return String(cell?.element?.description?._text ?? '').trim()
}

function isEmptyVisibleCaptionLabel(cell: GridRow | undefined): boolean {
  return isLabelGridCell(cell) && captionVisibleText(cell) === ''
}

/** Field after caption, or hint Label after a blank caption cell (not two real captions). */
function isCaptionColumnFollower(caption: GridRow | undefined, next: GridRow | undefined): boolean {
  if (isFieldAfterEmptyLabel(next)) return true
  if (!caption || !next) return false
  return isEmptyVisibleCaptionLabel(caption) && isLabelGridCell(next)
}

function nextRealLayoutCell(row: GridRow[], index: number): GridRow | undefined {
  for (let j = index + 1; j < row.length; j++) {
    const cell = row[j]
    if (isColspanPlaceholder(cell) || isGridColumnSpacer(cell)) continue
    if (!cell?.element) continue
    return cell
  }
  return undefined
}

function countElementCellsInRow(row: GridRow[]): number {
  let n = 0
  for (const cell of row) {
    if (!cell?.element || isColspanPlaceholder(cell) || isGridColumnSpacer(cell)) continue
    n++
  }
  return n
}

/**
 * Swing: empty-width Label before a field uses preferred size (hug), not 50% share.
 * Only simple 2-element rows (Label + field), e.g. panel 4266 — not multi-column forms (4270)
 * where hug would desync shared column tracks. Align (left/right) does not matter: a right
 * caption still hugs so the field can grow.
 */
function markEmptyCaptionLabelsAsContent(row: GridRow[]): boolean {
  if (countElementCellsInRow(row) !== 2) return false

  let marked = false
  for (let i = 0; i < row.length; i++) {
    const cell = row[i]
    if (!isLabelGridCell(cell) || isColspanPlaceholder(cell)) continue
    const colspan = parseInt(String(cell._attributes?.colspan || '1'), 10) || 1
    if (colspan > 1) continue
    if (parseTdWidth(cell._attributes?.width).kind !== 'empty') continue
    const align = String(cell.element?.align?._text ?? 'left').trim().toLowerCase()
    if (align === 'center') continue

    const next = nextRealLayoutCell(row, i)
    if (!isCaptionColumnFollower(cell, next)) continue

    if (!cell._attributes) cell._attributes = {}
    cell._attributes.widthMode = 'content'
    cell._attributes.cols = 0
    marked = true
  }
  return marked
}

function captionLabelText(cell: GridRow): string {
  return String(cell.element?.description?._text || cell.element?.name?._text || '').trim()
}

/** Rough % width so the longest caption fits; used to align field starts across rows. */
function estimateCaptionLabelColPct(text: string): number {
  const n = [...text].length
  return Math.min(32, Math.max(12, Math.ceil(n * 1.25 + 4)))
}

type SimpleCaptionRow = { label: GridRow; field: GridRow }

function collectSimpleCaptionContentRows(rows: GridRow[][]): SimpleCaptionRow[] {
  const out: SimpleCaptionRow[] = []
  for (const row of rows) {
    if (!Array.isArray(row) || countElementCellsInRow(row) !== 2) continue
    let label: GridRow | undefined
    let field: GridRow | undefined
    for (let i = 0; i < row.length; i++) {
      const cell = row[i]
      if (!cell?.element || isColspanPlaceholder(cell) || isGridColumnSpacer(cell)) continue
      if (isLabelGridCell(cell) && cell._attributes?.widthMode === 'content') {
        const next = nextRealLayoutCell(row, i)
        if (isCaptionColumnFollower(cell, next) && next) {
          label = cell
          field = next
        }
      }
    }
    if (label && field) out.push({ label, field })
  }
  return out
}

/**
 * After per-row hug: give all simple caption rows the same label column width
 * so field left edges align (web form layout; Swing stays jagged by preferred size).
 */
function alignSimpleCaptionLabelColumns(rows: GridRow[][]): void {
  const captionRows = collectSimpleCaptionContentRows(rows)
  if (captionRows.length < 2) return

  let labelPct = 12
  for (const { label } of captionRows) {
    labelPct = Math.max(labelPct, estimateCaptionLabelColPct(captionLabelText(label)))
  }
  const fieldPct = 100 - labelPct

  for (const { label, field } of captionRows) {
    if (!label._attributes) label._attributes = {}
    if (!field._attributes) field._attributes = {}
    delete label._attributes.widthMode
    label._attributes.cols = labelPct
    field._attributes.widthMode = 'grow'
    field._attributes.cols = fieldPct
  }
}

function hasInlineFieldBefore(row: GridRow[], index: number): boolean {
  for (let j = index - 1; j >= 0; j--) {
    const cell = row[j]
    if (isColspanPlaceholder(cell)) continue
    if (isGridColumnSpacer(cell)) continue
    if (!cell?.element) return false
    return isInlineFieldCell(cell)
  }
  return false
}

function labelMinColPct(cell: GridRow | undefined): number | null {
  if (!isLabelGridCell(cell)) return null
  const align = String(cell.element?.align?._text ?? 'left').trim().toLowerCase()
  if (align === 'right' || align === 'center') return LABEL_AFTER_FIELD_MIN_COL_PCT
  return null
}

function minColPctForCell(cell: GridRow | undefined, row?: GridRow[]): number | null {
  // Swing: content width (px / bare+empty) → weight 0; do not inflate ListBox/Date min %
  if (isContentWidthCell(cell, row)) return null
  if (isDateTimeGridCell(cell)) {
    return resolveDateTimeMinColPct(cell?.element?.format?._text)
  }
  if (isListLikeGridCell(cell)) {
    const colspan = parseInt(String(cell?._attributes?.colspan || '1'), 10) || 1
    return colspan >= 2 ? LIST_LIKE_COLSPAN_MIN_COL_PCT : LIST_LIKE_MIN_COL_PCT
  }
  return null
}

/** Logical columns that are content/hug in at least one row (px or bare+empty). */
function collectContentWidthLogicalCols(rows: GridRow[][], maxCol: number): Set<number> {
  const fixed = new Set<number>()
  for (const row of rows) {
    if (!Array.isArray(row)) continue
    let col = 0
    for (const cell of row) {
      if (isColspanPlaceholder(cell)) continue
      const colspan = parseInt(String(cell?._attributes?.colspan || '1'), 10) || 1
      if (isContentWidthCell(cell, row)) {
        for (let i = 0; i < colspan && col + i < maxCol; i++) {
          fixed.add(col + i)
        }
      }
      col += colspan
    }
  }
  return fixed
}

function shareWeightFromCell(cell: GridRow | undefined, row?: GridRow[]): number {
  if (isContentWidthCell(cell, row)) return 0
  const parsed = parseTdWidth(cell?._attributes?.width)
  if (parsed.kind === 'share' || parsed.kind === 'percent') return parsed.value
  return 0
}

/** After % assignment: mark Swing-like content/grow width modes on cells. */
function markTdWidthModes(rows: GridRow[][]): void {
  for (const row of rows) {
    if (!Array.isArray(row)) continue
    let hasContent = markEmptyCaptionLabelsAsContent(row)
    for (const cell of row) {
      if (!cell || isColspanPlaceholder(cell)) continue
      if (!cell._attributes) cell._attributes = {}
      if (cell._attributes.widthMode === 'content') {
        hasContent = true
        continue
      }
      if (isContentWidthCell(cell, row)) {
        cell._attributes.widthMode = 'content'
        cell._attributes.cols = 0
        hasContent = true
      }
    }
    if (!hasContent) continue

    for (const cell of row) {
      if (!cell || isColspanPlaceholder(cell)) continue
      if (cell._attributes?.widthMode === 'content') continue
      if (!cell._attributes) cell._attributes = {}
      cell._attributes.widthMode = 'grow'
    }

    const growSum = row.reduce((acc, cell) => {
      if (!cell || isColspanPlaceholder(cell)) return acc
      if (cell._attributes?.widthMode === 'content') return acc
      return acc + (Number(cell._attributes?.cols) || 0)
    }, 0)
    if (growSum <= 0) {
      const growCells = row.filter(
        (c) => c && !isColspanPlaceholder(c) && c._attributes?.widthMode !== 'content',
      )
      const share = growCells.length ? 100 / growCells.length : 0
      for (const cell of growCells) {
        if (!cell._attributes) cell._attributes = {}
        cell._attributes.cols = share
      }
    } else if (Math.abs(growSum - 100) > 0.01) {
      for (const cell of row) {
        if (!cell?._attributes || cell._attributes.widthMode === 'content') continue
        if (isColspanPlaceholder(cell)) continue
        const c = Number(cell._attributes.cols) || 0
        cell._attributes.cols = (100 * c) / growSum
      }
    }
  }

  alignSimpleCaptionLabelColumns(rows)
}

function donorTakeWeight(cell: GridRow | undefined, index: number, row: GridRow[]): number {
  if (!cell?.element) {
    const hasEarlyDateTime = row.some((c, i) => i > 0 && i <= 2 && isDateTimeGridCell(c))
    if (hasEarlyDateTime && index === 0) return 0.35
    return 2
  }
  if (isLabelGridCell(cell)) {
    if (hasListLikeAhead(row, index)) return 0.12
    return 0.6
  }
  return 1
}

function sumGlobalColRange(globalColPct: number[], startCol: number, span: number): number {
  let sum = 0
  for (let j = 0; j < span; j++) {
    sum += globalColPct[startCol + j] ?? 0
  }
  return sum
}

function renormalizeGlobalColPct(globalColPct: number[]): void {
  const sum = globalColPct.reduce((acc, v) => acc + v, 0)
  if (sum <= 0 || Math.abs(sum - 100) < 0.01) return
  for (let i = 0; i < globalColPct.length; i++) {
    globalColPct[i] = (100 * globalColPct[i]) / sum
  }
}

function findCellCoveringColumn(
  rows: GridRow[][],
  colIdx: number,
): { cell?: GridRow; row: GridRow[]; cellIndex: number } {
  for (const row of rows) {
    let logicalCol = 0
    for (let i = 0; i < row.length; i++) {
      const cell = row[i]
      if (!cell || isColspanPlaceholder(cell)) {
        if (isColspanPlaceholder(cell)) logicalCol++
        continue
      }
      const colspan = parseInt(String(cell._attributes?.colspan || '1'), 10) || 1
      if (colIdx >= logicalCol && colIdx < logicalCol + colspan) {
        return { cell, row, cellIndex: i }
      }
      logicalCol += colspan
    }
  }
  return { row: [], cellIndex: -1 }
}

function takeFromGlobalColDonors(
  globalColPct: number[],
  rows: GridRow[][],
  extraNeeded: number,
  canDonateCol: (colIdx: number) => boolean,
): void {
  if (extraNeeded <= 0) return

  const donors: { index: number; slack: number; weight: number }[] = []
  for (let c = 0; c < globalColPct.length; c++) {
    if (!canDonateCol(c)) continue
    const slack = globalColPct[c] - COLWIDTH_DONOR_FLOOR_PCT
    if (slack <= 0) continue
    const { cell, row, cellIndex } = findCellCoveringColumn(rows, c)
    const weight = donorTakeWeight(cell, cellIndex, row) * slack
    donors.push({ index: c, slack, weight })
  }

  const weightedSlack = donors.reduce((acc, d) => acc + d.weight, 0)
  if (weightedSlack <= 0) return

  const takeTotal = Math.min(extraNeeded, donors.reduce((acc, d) => acc + d.slack, 0))
  let distributed = 0

  for (let d = 0; d < donors.length; d++) {
    const donor = donors[d]
    const isLast = d === donors.length - 1
    const share = isLast
      ? takeTotal - distributed
      : (takeTotal * donor.weight) / weightedSlack
    const actual = Math.min(donor.slack, share)
    globalColPct[donor.index] -= actual
    distributed += actual
  }
}

function boostGlobalColRange(
  globalColPct: number[],
  rows: GridRow[][],
  startCol: number,
  span: number,
  minSum: number,
  canDonateCol: (colIdx: number) => boolean,
): void {
  const current = sumGlobalColRange(globalColPct, startCol, span)
  if (current >= minSum) return
  const extra = minSum - current
  for (let j = 0; j < span; j++) {
    globalColPct[startCol + j] = (globalColPct[startCol + j] ?? 0) + extra / span
  }
  takeFromGlobalColDonors(globalColPct, rows, extra, canDonateCol)
}

/** Доли логических колонок панели */
function computeGlobalColPct(rows: GridRow[][]): number[] {
  const maxCol = Math.max(0, ...rows.map((row) => (Array.isArray(row) ? rowLogicalSpan(row) : 0)))
  if (maxCol === 0) return [100]

  const base = new Array<number>(maxCol).fill(0)
  const fixedCols = collectContentWidthLogicalCols(rows, maxCol)

  for (const row of rows) {
    if (!Array.isArray(row)) continue
    let col = 0
    for (const cell of row) {
      if (isColspanPlaceholder(cell)) continue
      const width = shareWeightFromCell(cell, row)
      const colspan = parseInt(String(cell._attributes?.colspan || '1'), 10) || 1
      if (width > 0) {
        const share = width / colspan
        for (let i = 0; i < colspan && col + i < maxCol; i++) {
          if (fixedCols.has(col + i)) continue
          base[col + i] = Math.max(base[col + i], share)
        }
      }
      col += colspan
    }
  }

  for (let i = 0; i < maxCol; i++) {
    if (fixedCols.has(i)) {
      base[i] = 0
    } else if (!base[i]) {
      base[i] = 1
    }
  }

  const total = base.reduce((a, b) => a + b, 0) || 1
  return base.map((b) => (100 * b) / total)
}

function applyGlobalColPctToRow(row: GridRow[], globalColPct: number[]): void {
  let logicalCol = 0
  for (let i = 0; i < row.length; i++) {
    const cell = row[i]
    if (!cell) continue
    if (isColspanPlaceholder(cell)) continue

    const colspan = parseInt(String(cell._attributes?.colspan || '1'), 10) || 1
    const sum = sumGlobalColRange(globalColPct, logicalCol, colspan)
    if (!cell._attributes) cell._attributes = {}
    cell._attributes.cols = sum

    for (let j = 1; j < colspan; j++) {
      const ph = row[i + j]
      if (ph && isColspanPlaceholder(ph)) {
        if (!ph._attributes) ph._attributes = {}
        ph._attributes.cols = 0
      }
    }

    logicalCol += colspan
    i += colspan - 1
  }
}

/**
 * Минимальные ширины Date/ListBox/label — на общих долях колонок панели
 */
function applyElementMinColWidthsToGlobal(globalColPct: number[], rows: GridRow[][]): void {
  const protectedCols = new Set<number>()

  for (const row of rows) {
    if (!Array.isArray(row)) continue
    let logicalCol = 0
    for (let i = 0; i < row.length; i++) {
      const cell = row[i]
      if (!cell || isColspanPlaceholder(cell)) continue

      const colspan = parseInt(String(cell._attributes?.colspan || '1'), 10) || 1
      const minPct = minColPctForCell(cell, row)
      if (minPct != null) {
        for (let j = 0; j < colspan; j++) protectedCols.add(logicalCol + j)
        boostGlobalColRange(
          globalColPct,
          rows,
          logicalCol,
          colspan,
          minPct,
          (c) => !protectedCols.has(c),
        )
      }

      if (isLabelGridCell(cell) && colspan === 1 && hasListLikeAhead(row, i)) {
        protectedCols.add(logicalCol)
        if (globalColPct[logicalCol] < LABEL_BEFORE_LIST_MIN_COL_PCT) {
          const extra = LABEL_BEFORE_LIST_MIN_COL_PCT - globalColPct[logicalCol]
          globalColPct[logicalCol] = LABEL_BEFORE_LIST_MIN_COL_PCT
          takeFromGlobalColDonors(
            globalColPct,
            rows,
            extra,
            (c) => !protectedCols.has(c),
          )
        }
      }

      const labelMin =
        isLabelGridCell(cell) && colspan === 1
          ? labelMinColPct(cell) ?? (hasInlineFieldBefore(row, i) ? LABEL_AFTER_FIELD_MIN_COL_PCT : null)
          : null
      if (labelMin != null) {
        protectedCols.add(logicalCol)
        if (globalColPct[logicalCol] < labelMin) {
          const extra = labelMin - globalColPct[logicalCol]
          globalColPct[logicalCol] = labelMin
          takeFromGlobalColDonors(globalColPct, rows, extra, (c) => !protectedCols.has(c))
        }
      }

      logicalCol += colspan
      i += colspan - 1
    }
  }

  renormalizeGlobalColPct(globalColPct)
}

function applyElementMinColWidths(rows: GridRow[][]): GridRow[][] {
  if (!Array.isArray(rows)) return rows
  const out = rows.map((row) => {
    if (!Array.isArray(row)) return row
    return row.map((cell) =>
      cell && typeof cell === 'object'
        ? { ...cell, _attributes: { ...(cell._attributes || {}) } }
        : cell,
    )
  })

  const globalColPct = computeGlobalColPct(out)
  applyElementMinColWidthsToGlobal(globalColPct, out)
  for (const row of out) {
    if (Array.isArray(row)) applyGlobalColPctToRow(row, globalColPct)
  }
  markTdWidthModes(out)
  return out
}

export interface GridResult {
  rows: number[][]
}

/** Сумма colspan по строке (логическое число колонок). */
function rowLogicalSpan(row: GridRow[] | any[]): number {
  if (!Array.isArray(row)) return 0
  return row.reduce((acc, c) => {
    if (isColspanPlaceholder(c)) return acc
    const colspan = parseInt(String(c?._attributes?.colspan || '1'), 10) || 1
    return acc + colspan
  }, 0)
}

/**
 * Дополняет каждую строку пустыми ячейками справа до max логических колонок.
 */
function padRowsToMaxLogicalColumns(rows: GridRow[][]): GridRow[][] {
  if (!rows?.length) return rows
  const maxCols = Math.max(0, ...rows.map((row) => (Array.isArray(row) ? rowLogicalSpan(row) : 0)))
  if (maxCols === 0) return rows

  return rows.map((row) => {
    if (!Array.isArray(row)) return row
    const span = rowLogicalSpan(row)
    const out = [...row]
    for (let i = span; i < maxCols; i++) {
      out.push({
        _attributes: {},
        _isGridColumnSpacer: true,
      } as GridRow)
    }
    return out
  })
}

export const gridService = {
  parseTdWidth,
  resolveTdWidthKind,
  rowLogicalSpan,
  padRowsToMaxLogicalColumns,

  /**
   * Глобальная сетка: доли колонок берутся по всем строкам,
   * затем для каждой ячейки — сумма долей покрытых колонок.
   */
  grid(rows: GridRow[][]): number[][] {
    if (!rows.length) return []

    const maxCol = Math.max(0, ...rows.map((row) => (Array.isArray(row) ? rowLogicalSpan(row) : 0)))
    if (maxCol === 0) {
      return rows.map(() => [100])
    }

    const base = new Array<number>(maxCol).fill(0)
    const fixedCols = collectContentWidthLogicalCols(rows, maxCol)

    for (const row of rows) {
      if (!Array.isArray(row)) continue
      let col = 0
      for (const cell of row) {
        if (isColspanPlaceholder(cell)) continue
        const width = shareWeightFromCell(cell, row)
        const colspan = parseInt(String(cell._attributes?.colspan || '1'), 10) || 1
        if (width > 0) {
          const share = width / colspan
          for (let i = 0; i < colspan && col + i < maxCol; i++) {
            const idx = col + i
            if (fixedCols.has(idx)) continue
            base[idx] = Math.max(base[idx], share)
          }
        }
        col += colspan
      }
    }

    for (let i = 0; i < maxCol; i++) {
      if (fixedCols.has(i)) {
        base[i] = 0
      } else if (!base[i]) {
        base[i] = 1
      }
    }

    const total = base.reduce((a, b) => a + b, 0) || 1
    const colPct = base.map((b) => (100 * b) / total)

    return rows.map((row) => {
      if (!Array.isArray(row)) return [100]

      let col = 0
      const out: number[] = []
      for (const cell of row) {
        if (isColspanPlaceholder(cell)) continue
        const colspan = parseInt(String(cell._attributes?.colspan || '1'), 10) || 1
        let sum = 0
        for (let i = 0; i < colspan; i++) {
          sum += colPct[col + i] ?? 0
        }
        out.push(sum)
        col += colspan
      }

      const s = out.reduce((a, b) => a + b, 0)
      if (s > 0 && Math.abs(s - 100) > 0.001) {
        return out.map((x) => (100 * x) / s)
      }
      return out
    })
  },

  /**
   * Перераспределяет пустые колонки (строковый уровень)
   */
  redistributeEmpty(cols: number[]): number[] {
    const fullWidth = cols.reduce((sum, i) => sum + (i > 1 ? i : 0), 0)
    const countEmpty = cols.reduce((count, i) => count + (i <= 1 ? 1 : 0), 0)

    if (fullWidth < 100 && countEmpty > 0) {
      const share = (100 - fullWidth) / countEmpty
      return cols.map((col) => (col <= 1 ? share : col))
    }

    return cols
  },

  /**
   * Суммирует доли колонок на ячейку с colspan; плейсхолдеры colspan получают cols: 0.
   */
  mergeColspanCellWidths(rows: GridRow[][]): GridRow[][] {
    if (!Array.isArray(rows)) return rows

    return rows.map((row) => {
      if (!Array.isArray(row)) return row

      const out = row.map((cell) => {
        if (!cell || typeof cell !== 'object') return cell
        return {
          ...cell,
          _attributes: { ...(cell._attributes || {}) },
        }
      })

      for (let i = 0; i < out.length; i++) {
        const cell = out[i] as GridRow
        if (!cell || (cell as { _isColspanPlaceholder?: boolean })._isColspanPlaceholder) continue

        const colspan = parseInt(String(cell._attributes?.colspan || '1'), 10) || 1
        if (colspan <= 1) continue

        let sum = Number(cell._attributes?.cols) || 0
        for (let j = 1; j < colspan && i + j < out.length; j++) {
          const next = out[i + j] as GridRow & { _isColspanPlaceholder?: boolean }
          if (next?._isColspanPlaceholder) {
            sum += Number(next._attributes?.cols) || 0
          }
        }
        if (!cell._attributes) cell._attributes = {}
        cell._attributes.cols = sum

        for (let j = 1; j < colspan && i + j < out.length; j++) {
          const next = out[i + j] as GridRow & { _isColspanPlaceholder?: boolean }
          if (next?._isColspanPlaceholder) {
            if (!next._attributes) next._attributes = {}
            next._attributes.cols = 0
          }
        }
      }

      return out
    })
  },

  /**
   * Выравнивает строки по max колонкам и добавляет атрибут cols каждой ячейке.
   */
  setColsByGrid(rows: GridRow[][], gridByHead?: number[][]): GridRow[][] {
    if (!Array.isArray(rows)) rows = [rows]
    const working = gridByHead ? rows : padRowsToMaxLogicalColumns(rows)
    const grid = gridByHead ?? this.grid(working)

    const out = working.map((row, i) => {
      if (!Array.isArray(row)) row = [row]

      const cols = gridByHead ? grid[0] : grid[i]
      const rowSize = row.length

      cols.forEach((col, colIndex) => {
        if (colIndex < rowSize) {
          if (!row[colIndex]) {
            row[colIndex] = {
              _attributes: { cols: col },
              attrs: {},
              rows: [],
            }
          } else {
            if (!row[colIndex]._attributes) {
              row[colIndex]._attributes = {}
            }
            row[colIndex]._attributes.cols = col
          }
        } else {
          row.push({
            _attributes: { cols: col },
            attrs: {},
            rows: [],
          })
        }
      })

      return row
    })
    markTdWidthModes(out)
    return out
  },

  /**
   * setColsByGrid + суммирование colspan для элементов
   */
  setColsByGridWithColspan(rows: GridRow[][]): GridRow[][] {
    return applyElementMinColWidths(this.mergeColspanCellWidths(this.setColsByGrid(rows)))
  },

  applyElementMinColWidths,
}

export default gridService
